// TaxiLiveMap — mini-carte Leaflet affichant le trajet OSRM entre pickup et dropoff.
// Reçoit directement une `geometry` (liste [[lat, lng], ...]) renvoyée par
// `/api/taxi/calculate-fare`. En cas de fallback haversine (geometry=null),
// trace une ligne pointillée directe pour signaler l'imprécision.

import { useEffect, useMemo, useRef } from "react";
import { DarkTiles } from "@/components/map/DarkTiles";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Icônes custom (évite les PNG manquants par défaut de Leaflet)
const pickupIcon = L.divIcon({
  className: "woma-pickup-icon",
  html: `<div style="width:30px;height:30px;border-radius:50%;background:#003594;border:3px solid #D4AF37;box-shadow:0 4px 12px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#D4AF37;font-weight:900;font-size:14px;">D</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

const dropoffIcon = L.divIcon({
  className: "woma-dropoff-icon",
  html: `<div style="width:30px;height:30px;border-radius:50%;background:#D4AF37;border:3px solid #003594;box-shadow:0 4px 12px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#003594;font-weight:900;font-size:14px;">A</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

// Composant interne : auto-fit la carte à la polyline / bounds
function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions || positions.length === 0) return;
    try {
      const bounds = L.latLngBounds(positions);
      if (bounds.isValid()) {
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
      }
    } catch (e) {
      // noop — bornes invalides
    }
  }, [positions, map]);
  return null;
}

export default function TaxiLiveMap({
  pickup,          // {lat, lng}
  dropoff,         // {lat, lng}
  geometry,        // [[lat, lng], ...] si OSRM OK, sinon null/undefined
  distanceSource,  // "osrm" | "haversine" | null
  className = "",
}) {
  const containerRef = useRef(null);

  const route = useMemo(() => {
    if (Array.isArray(geometry) && geometry.length >= 2) return geometry;
    if (
      pickup?.lat != null && pickup?.lng != null &&
      dropoff?.lat != null && dropoff?.lng != null
    ) {
      return [[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]];
    }
    return null;
  }, [geometry, pickup, dropoff]);

  const hasPoints = pickup?.lat != null && dropoff?.lat != null;
  const center = hasPoints
    ? [(pickup.lat + dropoff.lat) / 2, (pickup.lng + dropoff.lng) / 2]
    : [14.6928, -17.4467]; // Plateau par défaut

  const isFallback = distanceSource === "haversine";

  if (!hasPoints) {
    return (
      <div
        className={`relative rounded-[2rem] overflow-hidden border border-white/10 bg-[#0B1E2B] flex items-center justify-center ${className}`}
        style={{ minHeight: 220 }}
        data-testid="taxi-live-map-empty"
      >
        <p className="text-xs text-slate-500 font-bold uppercase tracking-widest text-center px-6">
          Sélectionnez un départ et une destination pour afficher le trajet
        </p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`relative rounded-[2rem] overflow-hidden border border-[#D4AF37]/30 bg-[#0B1E2B] shadow-xl shadow-black/40 ${className}`}
      style={{ minHeight: 260 }}
      data-testid="taxi-live-map"
    >
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom={false}
        style={{ height: "100%", minHeight: 260, width: "100%" }}
        zoomControl={false}
        attributionControl={false}
      >
        <DarkTiles />
        <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon} />
        <Marker position={[dropoff.lat, dropoff.lng]} icon={dropoffIcon} />
        {route && (
          <Polyline
            positions={route}
            pathOptions={{
              color: isFallback ? "#F59E0B" : "#D4AF37",
              weight: 5,
              opacity: 0.9,
              dashArray: isFallback ? "8 10" : undefined,
            }}
          />
        )}
        <FitBounds positions={route} />
      </MapContainer>

      {/* Overlay badge source */}
      {distanceSource && (
        <div
          className={`absolute top-3 left-3 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-[0.25em] backdrop-blur-md border ${
            isFallback
              ? "bg-amber-500/20 border-amber-400/40 text-amber-200"
              : "bg-emerald-500/20 border-emerald-400/40 text-emerald-200"
          }`}
          data-testid="taxi-live-map-source"
        >
          {isFallback ? "Trajet approximatif" : "Trajet OSRM temps réel"}
        </div>
      )}
    </div>
  );
}
